import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import { I18n, Translate, Translate2 } from 'common'
import { breakpoints } from '../../../../../src/theme/breakpoints'
import { defaultTheme } from '../../../../../src/theme/index'
import { Button } from '../../../../../src/ui-components/forms/Button'
import { Price } from '../../../../../src/ui-components/products/Price'
import styled from 'styled-components'
import { useUserStore } from '../../../../../src/store/user/UserStore'

import InfoIcon from '@mui/icons-material/Info'
import { useNavigate } from 'react-router-dom'
import {
  hasErrors,
  handleBackToCart,
} from '../../../../../src/features/SummaryPanel/CommonFunctions'
import { IOrderConfirmationSummaryPannelProps } from '../../../../../src/features/SummaryPanel/Interfaces'
import OrderConfirmationVehicleInfo from './OrderConfirmationVehicleCartInfo'

export const OrderConfirmationSummaryPanel = observer(
  (props: IOrderConfirmationSummaryPannelProps): ReactElement => {
    const { header, vehicles, children, orderResponse, showYourCost } = props
    const userStore = useUserStore()
    const displayExclTax = userStore?.preferences?.displayExclTax === 'true'

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    const errors = !orderResponse || hasErrors(orderResponse)

    const backToCartHandler = async () => {
      setIsLoading(true)
      try {
        await handleBackToCart(orderResponse, navigate)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Back to cart navigation failed:', error)
        navigate('/cart')
      } finally {
        setIsLoading(false)
      }
    }
    return (
      <Wrapper id="summaryPanelContent">
        <Title>{Translate(header)}</Title>
        {vehicles?.map((vehicle) => (
          <OrderConfirmationVehicleInfo
            key={vehicle.vehicleName}
            {...vehicle}
            showYourCost={showYourCost}
          />
        ))}

        {showYourCost && (
          <>
            <Hr />
            <Total>
              <I18n name="grandTotal" />
              <Price num={orderResponse?.orderSummary?.grandTotal} />
            </Total>
            {displayExclTax && (
              <ExclTaxText>
                <I18n name="displayExclTax" />
              </ExclTaxText>
            )}
          </>
        )}

        {errors && (
          <>
            <ErrorMessageText>
              <ErrorMessageIcon>
                <InfoIcon sx={{ fontSize: '14px', color: '#D9001B' }} />
              </ErrorMessageIcon>
              <ErrorMessageIconText>
                <ErrorMessageInfoText>
                  <I18n name="" />
                  <span className="fw-bold">{Translate2('orderError')}</span>
                  {Translate2('orderConfirmationError')}`
                </ErrorMessageInfoText>
              </ErrorMessageIconText>
            </ErrorMessageText>
            <Button
              text="backToCart"
              minWidth="100%"
              disabled={isLoading}
              onClick={() => {
                backToCartHandler()
              }}
              loading={isLoading}
              style={{ marginTop: '12px' }}
            />
          </>
        )}

        {children}
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  background-color: ${(p) => p.theme.colors.white};
  font-family: Roboto, serif;
  font-size: 14px;
  padding: 14px;
  @media print, (max-width: ${breakpoints.laptop}px) {
    padding: 25px;
  }
`

const Title = styled.div`
  font-size: 24px;
  font-family: Oswald, serif;
  font-weight: bold;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  @media (max-width: ${breakpoints.laptop}px) {
    font-size: 20px;
  }
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  align-items: center;
`

const Hr = styled.hr`
  border-top: 1px solid ${(p) => p.theme.colors.almostBlack};
  opacity: 1;
  margin-bottom: 4px;
`

const ExclTaxText = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.almostBlack};
  text-align: right;
`
const ErrorMessageInfoText = styled.p`
  font-size: 11px;
  font-family: Roboto, serif;
  line-height: 12.89px;
  font-weight: 700px;
  margin: auto;
  text-align: justify;
  @media screen and (max-width: ${breakpoints.tablet}px) {
    font-size: 11px;
  }
`
const ErrorMessageText = styled.div`
  background-color: rgba(251, 230, 232, 1);
  display: flex;
  align-items: center;
  padding: 7px;
  margin-top: 24px;
`

const ErrorMessageIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`

const ErrorMessageIconText = styled.div`
  color: black;
`
